import './App.css';
import { Routes, Route, useLocation } from "react-router-dom";
import Home from './pages/Home/Home'
import Navigation from './components/Navigation/Navigation';
import { SiteBackgroundProvider, useSiteBackground } from './context/SiteBackgroundContext';
import SiteCanvasBackground from './components/SiteCanvasBackground/SiteCanvasBackground';
import ShootingStars from './components/ShootingStars/ShootingStars';
import Footer from './components/Footer/Footer';
import ScrollToTop from './ScrollToTop';
import ReactGA from 'react-ga';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import SpaceLoader from './components/SpaceLoader/SpaceLoader';
import {
  WaveWipe,
  WAVE_COVER_MS,
  WAVE_HOLD_MS,
  WAVE_REVEAL_MS,
  getRouteAccent,
} from './transition';
import {
  readPendingHomeSection,
  scrollToHomeSection,
} from './navigation/homeSections';
import { prefersLiteEffects } from './utils/perf';

const CrimsonFlags = lazy(() => import('./Projects/CrimsonFlags/CrimsonFlags'));
const MenuMaker = lazy(() => import('./Projects/MenuMaker/MenuMaker'));
const IGWS = lazy(() => import('./Projects/IGWS/IGWS'));
const Binti = lazy(() => import('./Projects/Binti/Binti'));
const ElectricFox = lazy(() => import('./Projects/ElectricFox/ElectricFox'));
const Resume = lazy(() => import('./Projects/Resume/Resume'));
const QFWellness = lazy(() => import('./Projects/QFWellness/QFWellness'));
const DiningWithTara = lazy(() => import('./Projects/DiningWithTara/DiningWithTara'));

const sameLocation = (a, b) =>
  a.pathname === b.pathname && a.search === b.search && a.hash === b.hash;

function AppShell() {
  const location = useLocation();
  const { setIsSiteReady } = useSiteBackground();
  // Content location freezes until the wipe has fully sealed the screen
  const [displayLocation, setDisplayLocation] = useState(location);
  const [wipePhase, setWipePhase] = useState('idle');
  // Destination brand tint — locked when a cover begins so it doesn't flicker mid-wipe
  const [wipeAccent, setWipeAccent] = useState(null);
  const [wipeCycle, setWipeCycle] = useState(0);
  const pendingLocation = useRef(location);
  const [useLiteEffects] = useState(() => prefersLiteEffects());

  const isQuietSurface =
    displayLocation.pathname.startsWith('/projects') || displayLocation.pathname === '/resume';
  const showSiteFx = !isQuietSurface;
  const showHeavyFx = showSiteFx && !useLiteEffects;
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoaderExiting, setIsLoaderExiting] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState('KF · Kevin Fuller');

  useEffect(() => {
    ReactGA.pageview(location.pathname + location.search);
  }, [location]);

  const beginCover = (nextLocation) => {
    pendingLocation.current = nextLocation;
    setWipeAccent(getRouteAccent(nextLocation.pathname));
    setWipeCycle((n) => n + 1);
    setWipePhase('covering');
  };

  // URL changed → start (or queue) a cover → swap → reveal sequence
  useEffect(() => {
    pendingLocation.current = location;
    if (sameLocation(location, displayLocation)) return;
    if (wipePhase === 'idle') {
      beginCover(location);
    }
  }, [location, displayLocation, wipePhase]);

  // After a reveal finishes, if the URL moved again, cover once more
  useEffect(() => {
    if (wipePhase !== 'idle') return;
    if (!sameLocation(location, displayLocation)) {
      beginCover(location);
    }
  }, [wipePhase, location, displayLocation]);

  // Cover complete → swap route under the seal
  useEffect(() => {
    if (wipePhase !== 'covering') return undefined;
    const id = window.setTimeout(() => {
      const next = pendingLocation.current;
      setDisplayLocation(next);
      setWipePhase('holding');
    }, WAVE_COVER_MS);
    return () => window.clearTimeout(id);
  }, [wipePhase]);

  // Hold under the seal until the new page has mounted + painted, then peel.
  // Reveal lag was mostly main-thread work from the destination page competing
  // with the wipe — settle that first while still fully covered.
  useEffect(() => {
    if (wipePhase !== 'holding') return undefined;

    const sectionId =
      displayLocation.pathname === '/'
        ? readPendingHomeSection(displayLocation)
        : null;

    let cancelled = false;
    let innerRaf = 0;
    let outerRaf = 0;
    let settleRaf = 0;
    let idleId = 0;
    let holdId = 0;

    if (sectionId) {
      outerRaf = window.requestAnimationFrame(() => {
        innerRaf = window.requestAnimationFrame(() => {
          if (!cancelled) {
            scrollToHomeSection(sectionId, { behavior: 'auto' });
          }
        });
      });
    }

    const startReveal = () => {
      if (cancelled) return;
      // Two frames so layout/paint from the new route flush before we animate
      settleRaf = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!cancelled) setWipePhase('revealing');
        });
      });
    };

    holdId = window.setTimeout(() => {
      if (cancelled) return;
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(startReveal, { timeout: 180 });
      } else {
        startReveal();
      }
    }, WAVE_HOLD_MS);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(outerRaf);
      window.cancelAnimationFrame(innerRaf);
      window.cancelAnimationFrame(settleRaf);
      window.clearTimeout(holdId);
      if (idleId && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [wipePhase, displayLocation]);

  // Reveal complete → idle
  useEffect(() => {
    if (wipePhase !== 'revealing') return undefined;
    const id = window.setTimeout(() => {
      setWipePhase('idle');
    }, WAVE_REVEAL_MS);
    return () => window.clearTimeout(id);
  }, [wipePhase]);

  // Keep the site mounted under the loader so the black hole can settle into
  // its scroll-driven frame before we fade the overlay away.
  useEffect(() => {
    const minimumLoaderTime = useLiteEffects ? 900 : 2000;
    const messageSwapTime = useLiteEffects ? 400 : 950;
    const exitTransitionTime = 450;
    const blackholeTimeoutMs = useLiteEffects ? 400 : 8000;
    const loadStartTime = Date.now();
    let messageTimeoutId;
    let revealTimeoutId;
    let exitTimeoutId;
    let blackholeTimeoutId;
    let settled = false;
    let pageLoaded = document.readyState === 'complete';
    // Quiet surfaces / lite mode skip waiting on the WebGPU layer.
    let blackholeReady = isQuietSurface || useLiteEffects;

    const tryFinishLoading = () => {
      if (settled || !pageLoaded || !blackholeReady) return;
      settled = true;

      const elapsedTime = Date.now() - loadStartTime;
      const remainingTime = Math.max(0, minimumLoaderTime - elapsedTime);

      revealTimeoutId = window.setTimeout(() => {
        setIsLoaderExiting(true);
        // Let hero text / entrance animations start as the overlay peels away.
        setIsSiteReady(true);
        exitTimeoutId = window.setTimeout(() => {
          setIsInitialLoading(false);
        }, exitTransitionTime);
      }, remainingTime);
    };

    const onPageLoad = () => {
      pageLoaded = true;
      tryFinishLoading();
    };

    const onBlackholeMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'blackhole-ready') return;
      blackholeReady = true;
      window.clearTimeout(blackholeTimeoutId);
      tryFinishLoading();
    };

    messageTimeoutId = window.setTimeout(() => {
      setLoaderMessage('KF · Kevin Fuller — entering site');
    }, messageSwapTime);

    if (pageLoaded) {
      onPageLoad();
    } else {
      window.addEventListener('load', onPageLoad, { once: true });
    }

    if (!isQuietSurface && !useLiteEffects) {
      window.addEventListener('message', onBlackholeMessage);
      blackholeTimeoutId = window.setTimeout(() => {
        blackholeReady = true;
        tryFinishLoading();
      }, blackholeTimeoutMs);
    }

    return () => {
      window.removeEventListener('load', onPageLoad);
      window.removeEventListener('message', onBlackholeMessage);
      window.clearTimeout(messageTimeoutId);
      window.clearTimeout(revealTimeoutId);
      window.clearTimeout(exitTimeoutId);
      window.clearTimeout(blackholeTimeoutId);
    };
  }, [isQuietSurface, useLiteEffects, setIsSiteReady]);

  useEffect(() => {
    if (!isInitialLoading) return undefined;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [isInitialLoading]);

  return (
    <>
      {showSiteFx ? <SiteCanvasBackground /> : null}
      <div className={`App${isQuietSurface ? ' App--quietSurface' : ''}`} id="App">
        {isInitialLoading ? (
          <SpaceLoader message={loaderMessage} isExiting={isLoaderExiting} />
        ) : null}
        {showHeavyFx ? <ShootingStars /> : null}
        <div className="appMainStack">
          <Navigation />
          <div className="route-frame">
            <ScrollToTop pathname={displayLocation.pathname} />
            <Suspense fallback={null}>
              <Routes location={displayLocation}>
                <Route index element={<Home />} />
                <Route path="/projects/crimsonflags" element={<CrimsonFlags />} />
                <Route path="/projects/menumaker" element={<MenuMaker />} />
                <Route path="/projects/igws" element={<IGWS />} />
                <Route path="/projects/bintibiz" element={<Binti />} />
                <Route path="/projects/electricfox" element={<ElectricFox />} />
                <Route path="/resume" element={<Resume />} />
                <Route path="/projects/qfwellness" element={<QFWellness />} />
                <Route
                  path="/projects/diningwithtara"
                  element={<DiningWithTara />}
                />
              </Routes>
            </Suspense>
          </div>
          <WaveWipe phase={wipePhase} accent={wipeAccent} cycle={wipeCycle} />
          <Footer />
        </div>
      </div>
    </>
  );
}

function App() {
  ReactGA.initialize('G-L3S365D76J');

  return (
    <SiteBackgroundProvider>
      <AppShell />
    </SiteBackgroundProvider>
  );
}

export default App;
